import { type ReactElement } from 'react'
import { useFormContext, useFormState } from 'react-hook-form'
import { TextField as MuiTextField, Stack } from '@mui/material'
import { SwitchLabeled } from '@ui/actions'
import { FormControl, TextField } from '@ui/forms'
import { buildHandInProjectName } from '../utils'

/**
 * The hand-in name is either composed from the description and the project
 * name (see `buildHandInProjectName`) and rewritten on every save, or typed
 * by hand -- the switch decides which, and travels with the project.
 */
const HandInProjectNameField = (): ReactElement => {
  const { watch, setValue } = useFormContext()
  const { errors } = useFormState()

  const isGenerated: boolean = watch('generate_hand_in_project_name')
  const composedName = buildHandInProjectName(watch('description'), watch('name'))

  // Set by the schema's object-level test when the composed name overruns the
  // backend column -- the preview field is read-only, so the error is the
  // only way to surface it (a typed name validates through its own field).
  const composedNameError = errors.hand_in_project_name?.message

  const handleModeChange = (generate: boolean) => {
    setValue('generate_hand_in_project_name', generate, { shouldValidate: true })
    // Taking over by hand starts from the composed name rather than from
    // whatever the field held before -- otherwise the value on screen would
    // change the moment the switch flips.
    if (!generate) {
      setValue('hand_in_project_name', composedName)
    }
  }

  return (
    <Stack direction="column" spacing={0.5}>
      {isGenerated ? (
        <FormControl
          label="Projektname für Abgabe"
          tooltip={{ text: 'Automatisch generiert aus Beschreibung und Projektname.' }}
          error={typeof composedNameError === 'string' ? composedNameError : undefined}
        >
          <MuiTextField
            value={composedName}
            size="small"
            disabled
            error={!!composedNameError}
            // Composed names run long -- two rows so the whole one is readable.
            multiline
            rows={2}
            data-cy="input-hand-in-project-name"
          />
        </FormControl>
      ) : (
        <TextField
          label="Projektname für Abgabe"
          name="hand_in_project_name"
          data-cy="input-hand-in-project-name"
          multiline
          rows={2}
        />
      )}
      <SwitchLabeled
        checked={isGenerated}
        onChange={event => handleModeChange(event.target.checked)}
        label="Projektname automatisch generieren"
        data-cy="switch-generate-hand-in-project-name"
      />
    </Stack>
  )
}

export default HandInProjectNameField
